// Renders the structured chart envelope returned by the Ask Your Data
// orchestrator. The LLM emits a small JSON spec of the form:
//
//   {
//     kind:   "line" | "bar" | "pie" | "candlestick",
//     title:  "<title>",
//     x:      "<x-axis key>",            // line/bar
//     y:      "<y-axis key>",            // line/bar
//     series: [{id, data: [{x, y}, ...]}],
//     slices: [{id, value}],             // pie only
//     candles: [{x, o, h, l, c}],        // candlestick only
//   }
//
// Styled to the Analyst sketch: soft teal/lime series, gradient area
// fills, hairline grid, faint 10px ticks and a bordered legend footer
// with square dots. The LLM downsamples to <= a few hundred points,
// so Nivo handles it without virtualisation. Candlesticks are drawn
// by hand in SVG (Nivo has no OHLC type) with the same axes/grid look.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';

import { surfacesFor, chartPaletteFor } from './uiTokens';


// Nivo theme derived from the sketch surfaces.
const nivoThemeFor = (t, s) => ({
  background: 'transparent',
  text: { fill: s.faint, fontSize: 10, fontFamily: t.typography.fontFamily },
  axis: {
    ticks: {
      line: { stroke: 'transparent' },
      text: { fill: s.faint, fontSize: 10, fontFamily: t.typography.fontFamily },
    },
    legend: { text: { fill: s.faint, fontSize: 10.5, fontFamily: t.typography.fontFamily } },
    domain: { line: { stroke: 'transparent' } },
  },
  grid: {
    line: {
      stroke: t.palette.mode === 'dark' ? '#232823' : 'rgba(0,0,0,0.08)',
      strokeWidth: 1,
    },
  },
  crosshair: {
    line: {
      stroke: t.palette.mode === 'dark' ? '#c0fb6e' : t.palette.primary.main,
      strokeWidth: 1,
      strokeOpacity: 0.5,
      strokeDasharray: '3 4',
    },
  },
  tooltip: {
    container: {
      background: s.panel2,
      color: s.text,
      fontSize: 11,
      fontFamily: t.typography.fontFamily,
      borderRadius: 8,
      border: `1px solid ${s.border2}`,
      boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
      padding: '6px 10px',
    },
  },
});


// Thin out point-scale ticks so long series don't smear the axis:
// at most ~7 evenly-spaced labels.
const thinTicks = (values, max = 7) => {
  if (!Array.isArray(values) || values.length <= max) return values;
  const step = Math.ceil(values.length / max);
  const out = values.filter((_, i) => i % step === 0);
  const last = values[values.length - 1];
  if (out[out.length - 1] !== last) out.push(last);
  return out;
};


// Bordered footer with square legend dots, like the sketch's
// panel-card .foot row.
const LegendFoot = ({ entries, s }) => {
  if (!entries || entries.length < 1) return null;
  return (
    <Stack
      direction="row" spacing={2.25} alignItems="center"
      sx={{
        pt: '10px', mt: '6px',
        borderTop: `1px solid ${s.border}`,
        flexWrap: 'wrap', rowGap: 0.5,
      }}
    >
      {entries.map(({ id, color }) => (
        <Stack key={id} direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11.5, color: s.faint, lineHeight: 1 }}>
            {id}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};


const renderLine = (chart, t, s, palette) => {
  const series = Array.isArray(chart.series) ? chart.series : [];
  const data = series
    .map((sr) => ({
      id: String(sr.id || 'series'),
      data: (Array.isArray(sr.data) ? sr.data : [])
        .map((p) => ({ x: p.x ?? p[chart.x], y: p.y ?? p[chart.y] }))
        .filter((p) => p.x !== undefined && p.y !== null && p.y !== undefined),
    }))
    .filter((sr) => sr.data.length > 0);
  if (!data.length) return null;

  // Optional forecast-interval band ({x, lo, hi} points). Drawn as a
  // shaded region behind the lines - the same visual language as the
  // goal forecast / inference pages - instead of quantile bounds as
  // extra line series.
  const bandPoints = (chart.band && Array.isArray(chart.band.data) ? chart.band.data : [])
    .map((p) => ({ x: p.x, lo: Number(p.lo), hi: Number(p.hi) }))
    .filter((p) => p.x !== undefined && Number.isFinite(p.lo) && Number.isFinite(p.hi));

  // Nivo's 'auto' y-domain only looks at the line series; widen it
  // manually so the band is never clipped off the chart.
  let yMin = 'auto';
  let yMax = 'auto';
  if (bandPoints.length) {
    const ys = data.flatMap((sr) => sr.data.map((p) => Number(p.y))).filter(Number.isFinite);
    const lo = Math.min(...ys, ...bandPoints.map((p) => p.lo));
    const hi = Math.max(...ys, ...bandPoints.map((p) => p.hi));
    const pad = (hi - lo || 1) * 0.05;
    yMin = lo - pad;
    yMax = hi + pad;
  }

  const bandColor = t.palette.mode === 'dark' ? '#c0fb6e' : t.palette.primary.main;
  // Custom Nivo layer: closed path upper-bound forward, lower-bound
  // back. Band x values that don't exist in the series' point scale
  // are skipped (the orchestrator aligns them with the series).
  const BandLayer = ({ xScale, yScale }) => {
    if (!bandPoints.length) return null;
    const pts = bandPoints
      .map((p) => ({ x: xScale(p.x), lo: yScale(p.lo), hi: yScale(p.hi) }))
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.lo) && Number.isFinite(p.hi));
    if (pts.length < 2) return null;
    const d = `M ${pts.map((p) => `${p.x} ${p.hi}`).join(' L ')} L ${
      [...pts].reverse().map((p) => `${p.x} ${p.lo}`).join(' L ')} Z`;
    return (
      <path
        d={d}
        fill={bandColor}
        fillOpacity={0.12}
        stroke={bandColor}
        strokeOpacity={0.25}
        strokeWidth={1}
      />
    );
  };

  const xValues = data[0].data.map((p) => p.x);
  const legend = [
    ...data.map((sr, i) => ({ id: sr.id, color: palette[i % palette.length] })),
    ...(bandPoints.length
      ? [{ id: String(chart.band?.id || 'interval'), color: bandColor }]
      : []),
  ];

  return (
    <>
      <Box sx={{ height: 240 }}>
        <ResponsiveLine
          data={data}
          theme={nivoThemeFor(t, s)}
          colors={palette}
          margin={{ top: 12, right: 16, bottom: 30, left: 44 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', stacked: false, min: yMin, max: yMax }}
          axisBottom={{ tickSize: 0, tickPadding: 10, tickValues: thinTicks(xValues) }}
          axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 5 }}
          enableGridX={false}
          gridYValues={4}
          curve="monotoneX"
          lineWidth={2.5}
          enablePoints={false}
          // Sketch look: a soft fill under a SINGLE series reads as a
          // glow; multiple overlapping fills stack into mud, so
          // multi-series charts stay crisp lines over the grid (and a
          // band already fills its own region).
          enableArea={data.length === 1 && !bandPoints.length}
          areaOpacity={0.12}
          layers={['grid', 'markers', 'axes', 'areas', BandLayer, 'crosshair', 'lines', 'points', 'slices', 'mesh', 'legends']}
          useMesh
          enableCrosshair
        />
      </Box>
      <LegendFoot entries={legend} s={s} />
    </>
  );
};


const renderBar = (chart, t, s, palette) => {
  const series = Array.isArray(chart.series) ? chart.series : [];
  // Convert Nivo-line-shaped series into bar-shaped rows: one row per
  // distinct x with one column per series.
  const xs = new Set();
  series.forEach((sr) => (sr.data || []).forEach((p) => xs.add(p.x ?? p[chart.x])));
  const keys = series.map((sr) => String(sr.id || 'series'));
  const data = Array.from(xs).map((x) => {
    const row = { __x: String(x) };
    series.forEach((sr) => {
      const point = (sr.data || []).find((p) => (p.x ?? p[chart.x]) === x);
      row[String(sr.id || 'series')] = point ? Number(point.y ?? point[chart.y]) : 0;
    });
    return row;
  });
  if (!data.length || !keys.length) return null;

  const xValues = data.map((r) => r.__x);
  const legend = keys.map((k, i) => ({ id: k, color: palette[i % palette.length] }));

  return (
    <>
      <Box sx={{ height: 240 }}>
        <ResponsiveBar
          data={data}
          keys={keys}
          indexBy="__x"
          theme={nivoThemeFor(t, s)}
          colors={palette}
          margin={{ top: 12, right: 16, bottom: 30, left: 44 }}
          padding={0.3}
          groupMode="grouped"
          borderRadius={3}
          axisBottom={{ tickSize: 0, tickPadding: 10, tickValues: thinTicks(xValues) }}
          axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 5 }}
          gridYValues={4}
          enableLabel={false}
        />
      </Box>
      <LegendFoot entries={legend} s={s} />
    </>
  );
};


const renderPie = (chart, t, s, palette) => {
  const slices = Array.isArray(chart.slices) ? chart.slices : [];
  const data = slices
    .map((sl) => ({ id: String(sl.id || 'slice'), label: String(sl.id || 'slice'), value: Number(sl.value || 0) }))
    .filter((sl) => Number.isFinite(sl.value) && sl.value > 0);
  if (!data.length) return null;

  const legend = data.slice(0, 6).map((d, i) => ({ id: d.id, color: palette[i % palette.length] }));

  return (
    <>
      <Box sx={{ height: 240 }}>
        <ResponsivePie
          data={data}
          theme={nivoThemeFor(t, s)}
          colors={palette}
          margin={{ top: 16, right: 70, bottom: 16, left: 70 }}
          innerRadius={0.55}
          padAngle={1.2}
          cornerRadius={4}
          activeOuterRadiusOffset={6}
          borderWidth={0}
          arcLinkLabelsTextColor={s.muted}
          arcLinkLabelsColor={{ from: 'color' }}
          arcLinkLabelsThickness={1.5}
          arcLabelsTextColor={t.palette.mode === 'dark' ? '#0a0c0a' : '#ffffff'}
          legends={[]}
        />
      </Box>
      <LegendFoot entries={legend} s={s} />
    </>
  );
};


// ---------------------------------------------------------------------------
// Candlestick (hand-drawn SVG — Nivo has no OHLC type)
// ---------------------------------------------------------------------------

const useMeasuredWidth = () => {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect?.width || 0;
      setW((prev) => (Math.abs(prev - cw) > 1 ? cw : prev));
    });
    ro.observe(el);
    setW(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

const fmtVal = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 10000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 100) return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// ~7 evenly spaced tick INDICES. The last index always shows, and it
// REPLACES its neighbour whenever they'd sit closer than a full step —
// two date labels one or two candles apart always collide.
const tickIndices = (n, max = 7) => {
  if (n <= max) return Array.from({ length: n }, (_, i) => i);
  const step = Math.ceil(n / max);
  const out = [];
  for (let i = 0; i < n; i += step) out.push(i);
  const last = n - 1;
  if (out[out.length - 1] !== last) {
    if (last - out[out.length - 1] < step) out[out.length - 1] = last;
    else out.push(last);
  }
  return out;
};

const CandlestickBody = ({ chart, t, s }) => {
  const [wrapRef, width] = useMeasuredWidth();
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  const candles = useMemo(() => (
    (Array.isArray(chart.candles) ? chart.candles : [])
      .map((cd) => ({
        x: String(cd.x), o: Number(cd.o), h: Number(cd.h),
        l: Number(cd.l), c: Number(cd.c),
      }))
      .filter((cd) => [cd.o, cd.h, cd.l, cd.c].every(Number.isFinite))
  ), [chart]);

  if (!candles.length) return null;

  const dark = t.palette.mode === 'dark';
  const up = dark ? '#5ee6d0' : '#197563';
  const down = dark ? '#ff7a6b' : '#d95f52';
  const gridColor = dark ? '#232823' : 'rgba(0,0,0,0.08)';

  const H = 240;
  const M = { top: 12, right: 16, bottom: 30, left: 44 };
  const innerW = Math.max(0, width - M.left - M.right);
  const innerH = H - M.top - M.bottom;

  let lo = Math.min(...candles.map((cd) => cd.l));
  let hi = Math.max(...candles.map((cd) => cd.h));
  if (hi === lo) { hi += 1; lo -= 1; }
  const pad = (hi - lo) * 0.05;
  lo -= pad; hi += pad;
  const yOf = (v) => M.top + innerH - (((v - lo) / (hi - lo)) * innerH);

  const band = candles.length ? innerW / candles.length : 0;
  const bodyW = Math.max(2, Math.min(14, band * 0.6));
  const ticks = [0, 1, 2, 3, 4].map((i) => lo + ((hi - lo) * i) / 4);
  const labelIdx = new Set(tickIndices(candles.length));

  const handleMove = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || !band) return;
    const px = e.clientX - rect.left - M.left;
    const i = Math.max(0, Math.min(candles.length - 1, Math.floor(px / band)));
    setHover(i);
  };

  const hovered = hover !== null ? candles[hover] : null;
  const tipLeft = hover !== null
    ? Math.max(80, Math.min(width - 80, M.left + hover * band + band / 2))
    : 0;

  return (
    <>
      <Box ref={wrapRef} sx={{ position: 'relative', width: '100%' }}>
        <svg
          ref={svgRef}
          width="100%"
          height={H}
          style={{ display: 'block' }}
          onMouseMove={handleMove}
          onMouseLeave={() => setHover(null)}
        >
          {innerW > 0 && (
            <>
              {ticks.map((tv, i) => (
                <g key={i}>
                  <line
                    x1={M.left} x2={M.left + innerW}
                    y1={yOf(tv)} y2={yOf(tv)}
                    stroke={gridColor} strokeWidth={1}
                  />
                  <text
                    x={M.left - 8} y={yOf(tv) + 3}
                    textAnchor="end" fontSize={10}
                    fill={s.faint} fontFamily={t.typography.fontFamily}
                  >
                    {fmtVal(tv)}
                  </text>
                </g>
              ))}

              {hover !== null && (
                <rect
                  x={M.left + hover * band} y={M.top}
                  width={band} height={innerH}
                  fill={dark ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.04)'}
                />
              )}

              {candles.map((cd, i) => {
                const cx = M.left + i * band + band / 2;
                const rising = cd.c >= cd.o;
                const color = rising ? up : down;
                const yTop = yOf(Math.max(cd.o, cd.c));
                const bodyH = Math.max(1.5, Math.abs(yOf(cd.o) - yOf(cd.c)));
                const showLabel = labelIdx.has(i);
                return (
                  <g key={i}>
                    <line
                      x1={cx} x2={cx}
                      y1={yOf(cd.h)} y2={yOf(cd.l)}
                      stroke={color} strokeWidth={1.5} strokeLinecap="round"
                    />
                    <rect
                      x={cx - bodyW / 2} y={yTop}
                      width={bodyW} height={bodyH}
                      rx={1.5} fill={color}
                      fillOpacity={rising ? 0.92 : 0.85}
                    />
                    {showLabel && (
                      <text
                        x={cx} y={H - 8}
                        textAnchor="middle" fontSize={10}
                        fill={s.faint} fontFamily={t.typography.fontFamily}
                      >
                        {cd.x}
                      </text>
                    )}
                  </g>
                );
              })}
            </>
          )}
        </svg>

        {hovered && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              left: tipLeft,
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
              bgcolor: s.panel2,
              color: s.text,
              border: `1px solid ${s.border2}`,
              borderRadius: '8px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
              px: 1.25, py: 0.75,
              fontSize: 11,
              whiteSpace: 'nowrap',
              zIndex: 2,
            }}
          >
            <Box sx={{ fontWeight: 600, mb: 0.25 }}>{hovered.x}</Box>
            <Box sx={{ display: 'flex', gap: 1.25 }}>
              <span style={{ color: s.faint }}>O</span> {fmtVal(hovered.o)}
              <span style={{ color: s.faint }}>H</span> {fmtVal(hovered.h)}
              <span style={{ color: s.faint }}>L</span> {fmtVal(hovered.l)}
              <span style={{ color: s.faint }}>C</span> {fmtVal(hovered.c)}
            </Box>
          </Box>
        )}
      </Box>
      <LegendFoot
        entries={[{ id: 'Up', color: up }, { id: 'Down', color: down }]}
        s={s}
      />
    </>
  );
};


const AskDataChart = ({ chart }) => {
  const theme = useTheme();
  const s = surfacesFor(theme);
  const palette = chartPaletteFor(theme);

  const body = useMemo(() => {
    if (!chart || typeof chart !== 'object') return null;
    const kind = String(chart.kind || '').toLowerCase();
    if (kind === 'line') return renderLine(chart, theme, s, palette);
    if (kind === 'bar')  return renderBar(chart, theme, s, palette);
    if (kind === 'pie')  return renderPie(chart, theme, s, palette);
    if (kind === 'candlestick' || kind === 'ohlc') {
      return <CandlestickBody chart={chart} t={theme} s={s} />;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, theme]);

  if (!body) {
    return (
      <Typography sx={{ fontSize: 12.5, color: s.faint, fontStyle: 'italic' }}>
        (chart unavailable — empty or unsupported shape)
      </Typography>
    );
  }
  return (
    <Box sx={{ width: '100%' }}>
      {chart?.title && (
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: s.text }}>
            {chart.title}
          </Typography>
        </Stack>
      )}
      {body}
    </Box>
  );
};

export default AskDataChart;
